import { centsToDollars, parsePrice } from "helpers/price";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { IRuleHistory } from "types/RuleHistory/RuleHistory";

import {
  formatHistoryValue,
  HISTORY_TYPE_LABELS,
  HISTORY_TYPE_SEVERITY,
} from "../contracts/helpers";

interface IRuleHistoryProps {
  histories?: IRuleHistory[];
}

/** Human labels for adjustment-rule fields across ruleset types. Unmapped fields
 * fall back to a Title-Cased version of the raw field name. */
const FIELD_LABELS: Record<string, string> = {
  providerId: "Provider",
  payerId: "Payer",
  groupName: "Rule Name",
  cptCodes: "CPT Codes",
  excludedCptCodes: "Excluded CPT Codes",
  discountPercentForRank2: "Rank 2 Discount %",
  discountPercentForRank3: "Rank 3 Discount %",
  discountPercentForRank4: "Rank 4 Discount %",
  discountPercentForRank5Plus: "Rank 5+ Discount %",
  allowOnDuplicateCptCodes: "Allow Duplicate CPT Codes",
  duplicateNotPaid: "Duplicate Not Paid",
  contractLanguage: "Contract Language",
  isDefault: "Default Rule",
  isPerDiem: "Per Diem",
  isBilateral: "Bilateral",
  totalRateInCents: "Total Rate",
  maxAmountInCents: "Max Amount",
  name: "Rule Name",
  priority: "Priority",
  active: "Active",
  suppressions: "Suppressions",
};

/** Fields stored in cents — rendered as USD in the history diff. */
const CENTS_FIELDS = new Set(["totalRateInCents", "maxAmountInCents"]);

/** Fields stored as a 0–1 fraction — rendered as a percentage (0.016 → 1.6%). */
const PERCENT_FRACTION_FIELDS = new Set(["percent"]);

/** Badge labels/severities — extends the shared contract-history maps with rule events. */
const TYPE_LABELS: Record<string, string> = {
  ...HISTORY_TYPE_LABELS,
  RuleAdded: "Rule Added",
  RuleRemoved: "Rule Removed",
};
const TYPE_SEVERITY: Record<string, "success" | "info" | "warning" | "danger"> =
  {
    ...HISTORY_TYPE_SEVERITY,
    RuleAdded: "success",
    RuleRemoved: "danger",
  };

const SUPPRESSION_TYPE_LABELS: Record<string, string> = {
  NOT_PAID: "Not Paid",
  PAY_BOTH: "Pay Both",
};

/** Render a suppression list snapshot as "Name (Type) — Suppression". */
const formatSuppressions = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return "None";
  return value
    .map((s: any) => {
      const type =
        SUPPRESSION_TYPE_LABELS[s?.suppressionType] ?? s?.suppressionType;
      return `${s?.rulesetName ?? "Unnamed"} (${
        s?.rulesetType ?? "?"
      }) — ${type}`;
    })
    .join("; ");
};

const toTitleCase = (field: string): string =>
  field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const labelFor = (field: string): string =>
  FIELD_LABELS[field] ?? toTitleCase(field);

const formatCents = (value: unknown): string => {
  const cents = Number(value);
  if (value === null || value === undefined || Number.isNaN(cents)) {
    return "Not set";
  }
  return parsePrice(centsToDollars(cents));
};

const formatPercentFraction = (value: unknown): string => {
  const fraction = Number(value);
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(fraction)
  ) {
    return "Not set";
  }
  // Trim float noise (0.016 * 100 -> 1.6000000000000001).
  return `${Number((fraction * 100).toFixed(4))}%`;
};

/** SCREAMING_SNAKE_CASE / enum-ish string → "Screaming Snake Case". */
const humanizeEnum = (s: string): string =>
  s
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Render an inpatient `config` value (or any nested config object) as readable
 * text: cents → dollars, *Percent → %, enum-ish strings humanized, nested objects
 * in parentheses. `key` drives per-field formatting.
 */
function formatConfig(value: unknown, key = ""): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => String(v)).join(", ") : "None";
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => {
        const rendered =
          v && typeof v === "object" && !Array.isArray(v)
            ? `(${formatConfig(v)})`
            : formatConfig(v, k);
        // Drop the "InCents" suffix from the label since the value shows dollars.
        const label = toTitleCase(k.replace(/InCents$/, ""));
        return `${label}: ${rendered}`;
      })
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (/InCents$/.test(key)) return parsePrice(centsToDollars(Number(value)));
  if (key === "percent" || /Percent$/.test(key)) return `${value}%`;
  if (typeof value === "string") return humanizeEnum(value);
  return String(value);
}

const formatRuleValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return formatHistoryValue(value);
};

/** Format a `from`/`to` value for a specific field (cents → dollars, suppressions → readable). */
const formatFieldValue = (field: string, value: unknown): string => {
  if (field === "suppressions") return formatSuppressions(value);
  if (field === "config") return formatConfig(value);
  if (CENTS_FIELDS.has(field)) return formatCents(value);
  if (PERCENT_FRACTION_FIELDS.has(field)) return formatPercentFraction(value);
  return formatRuleValue(value);
};

/** Edit history for a single adjustment rule: created/updated events, by who, and what changed. */
export default function RuleHistory({ histories }: IRuleHistoryProps) {
  // Oldest first.
  const sortedHistories = [...(histories ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <Card title="Rule History" className="mb-4">
      {sortedHistories.length > 0 ? (
        <div className="flex flex-column gap-3">
          {sortedHistories.map((entry, index) => (
            <div
              key={entry.id}
              className={
                index < sortedHistories.length - 1 ? "pb-3 border-bottom" : ""
              }
            >
              <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                <Badge
                  value={TYPE_LABELS[entry.type] ?? entry.type}
                  severity={TYPE_SEVERITY[entry.type] ?? "info"}
                />
                <span className="text-900 font-medium">
                  {entry.user?.name || entry.user?.email || "System"}
                </span>
                <span className="text-600 text-sm">
                  {new Date(entry.createdAt).toLocaleString()}
                </span>
              </div>
              {(entry.type === "AttachmentAdded" ||
                entry.type === "AttachmentRemoved") &&
                (entry.valueChanges as { fileName?: string } | null)
                  ?.fileName && (
                  <div className="text-sm mb-1">
                    <span className="font-medium text-900">Attachment:</span>{" "}
                    <span className="text-700">
                      {(entry.valueChanges as { fileName?: string }).fileName}
                    </span>
                  </div>
                )}
              {(entry.type === "RuleAdded" || entry.type === "RuleRemoved") &&
                (entry.valueChanges as { ruleName?: string } | null)
                  ?.ruleName && (
                  <div className="text-sm mb-1">
                    <span className="font-medium text-900">Rule:</span>{" "}
                    <span className="text-700">
                      {(entry.valueChanges as { ruleName?: string }).ruleName}
                    </span>
                  </div>
                )}
              {entry.type === "Updated" && entry.valueChanges && (
                <>
                  {(entry.valueChanges as { ruleName?: string }).ruleName && (
                    <div className="text-sm text-700 mb-1">
                      <span className="font-medium text-900">Rule:</span>{" "}
                      {(entry.valueChanges as { ruleName?: string }).ruleName}
                    </div>
                  )}
                  <ul className="list-none p-0 m-0 mt-2">
                    {Object.entries(entry.valueChanges)
                      .filter(([field]) => field !== "ruleName")
                      .map(([field, change]) => (
                        <li key={field} className="text-sm mb-1">
                          <span className="font-medium text-900">
                            {labelFor(field)}:
                          </span>{" "}
                          <span className="text-600">
                            {formatFieldValue(field, change.from)}
                          </span>
                          <i className="pi pi-arrow-right text-500 text-xs mx-2" />
                          <span className="text-900">
                            {formatFieldValue(field, change.to)}
                          </span>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-4 text-600">
          No history recorded for this rule yet.
        </div>
      )}
    </Card>
  );
}
